import { useCallback, useEffect, useMemo } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Modal, Skeleton } from '@mui/material';

import Typography from '@/components/Typography';
import { useAddToAppContext } from '@/features/Dashboard/context/AppContext';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import { TabSymbol } from '@/features/Dashboard/form/AppForm/context/types';
import { AppType } from '@/features/Dashboard/types';
import { useSpotCatalog } from '@/hooks/api/catalog';
import { useAppDispatch } from '@/hooks/redux-hooks';
import CatalogTable from '@/pages/Catalog/CatalogTable';
import { FilterProvider } from '@/pages/Catalog/contexts/filter/FilterContext';
import useCatalog from '@/pages/Catalog/useCatalog';
import {
  clearSelection,
  setForwardMode,
  setIsDashboardAppSelector
} from '@/slices/symbolSelectorSlice';
import { SelectedSymbol } from '@/types/symbols';
import generateSkeletonKeys from '@/utils/generateSkeletonKeys';
import getAmountLabel from '@/utils/getAmountLabel';

const skeletonKeys = generateSkeletonKeys(30);

const CatalogModalBase = ({
  addItemsToBacklog,
  setOpen
}: {
  addItemsToBacklog: (items: TabSymbol[]) => void;
  setOpen: (open: boolean) => void;
}) => {
  const { form } = useAppForm();
  const type = form.state.values?.type;
  const isForwardCurves = useMemo(() => type === AppType.FWD, [type]);
  const {
    filterOpen,
    setFilterOpen,
    table,
    expandedData,
    setExpandedData,
    showDiscontinued,
    setShowDiscontinued,
    status
  } = useCatalog(isForwardCurves);

  const { data: spotCatalog } = useSpotCatalog();

  const dispatch = useAppDispatch();

  // Enable special selector behavior only when Dashboard type is FWD
  useEffect(() => {
    dispatch(setIsDashboardAppSelector(true));
    dispatch(setForwardMode(isForwardCurves));
    return () => {
      dispatch(setForwardMode(false));
      dispatch(setIsDashboardAppSelector(false));
    };
  }, [dispatch, isForwardCurves]);

  const { setOnSubmit } = useAddToAppContext();

  const onSubmit = useCallback(
    (selectedSymbols: SelectedSymbol[]): void => {
      if (!selectedSymbols?.length) {
        return;
      }
      const symbolsToAdd: TabSymbol[] = selectedSymbols
        .map(symbol => {
          const spot = spotCatalog?.find(item => {
            const baseMatch =
              item.Symbol.Code === symbol.Code &&
              item.Symbol.PeriodType === symbol.PeriodType &&
              item.Symbol.TimeRef === symbol.TimeRef;
            if (!baseMatch) return false;
            if (isForwardCurves) return true; // ignore Period for FWD
            return item.Symbol.Period === symbol.Period;
          });

          if (!spot) return null;
          return {
            shortTitle: spot.Title || '',
            symbol,
            group: 'Prod_Indexes'
          };
        })
        .filter(Boolean) as TabSymbol[];

      addItemsToBacklog(symbolsToAdd);
      setOpen(false);
      dispatch(clearSelection());
      dispatch(setForwardMode(false));
    },
    [addItemsToBacklog, dispatch, isForwardCurves, setOpen, spotCatalog]
  );

  const catalogLabel = useMemo(() => {
    if (isForwardCurves) return 'curve';
    return expandedData ? 'item' : 'code';
  }, [expandedData, isForwardCurves]);

  useEffect(() => {
    setOnSubmit(() => (selectedSymbols: SelectedSymbol[]) => {
      onSubmit(selectedSymbols);
    });
    return () => {
      setOnSubmit(undefined);
    };
  }, [setOnSubmit, onSubmit]);

  const handleClose = useCallback(() => {
    setOpen(false);
    dispatch(clearSelection());
    dispatch(setForwardMode(false));
  }, [dispatch, setOpen]);

  return (
    <Modal
      open
      slotProps={{
        backdrop: {
          sx: {
            background: 'rgba(18, 22, 63, 0.80)',
            backdropFilter: 'blur(1px)'
          }
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClose={handleClose}
    >
      <Box
        sx={{
          width: '90%',
          backgroundColor: 'background.default',
          p: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            mt: 1
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-end',
              gap: 2
            }}
          >
            <Typography
              size={32}
              sx={{
                fontWeight: 700,
                lineHeight: 1
              }}
            >
              {isForwardCurves ? 'Forward curves selector' : 'Spot Selector'}
            </Typography>
            <Typography
              size={20}
              sx={{ lineHeight: 1.1 }}
            >
              {getAmountLabel(table.getFilteredRowModel().rows.length, catalogLabel)}
            </Typography>
          </Box>
          <Box
            aria-label='close selector'
            role='button'
            sx={{
              position: 'relative',
              bottom: '8px',
              cursor: 'pointer'
            }}
            onClick={handleClose}
          >
            <CloseIcon
              sx={{
                width: 32,
                height: 32
              }}
            />
          </Box>
        </Box>
        {status === 'loading' ? (
          <Box
            sx={{
              overflow: 'auto',
              maxHeight: '70vh'
            }}
          >
            {skeletonKeys.map(key => (
              <Skeleton
                key={key}
                height='23px'
                sx={{ my: 2 }}
                width='100%'
              />
            ))}
          </Box>
        ) : (
          <CatalogTable
            expandedData={expandedData}
            filterOpen={filterOpen}
            isForwardCurves={isForwardCurves}
            setExpandedData={setExpandedData}
            setFilterOpen={setFilterOpen}
            setShowDiscontinued={setShowDiscontinued}
            showDiscontinued={showDiscontinued}
            table={table}
          />
        )}
      </Box>
    </Modal>
  );
};

export const CatalogModal = ({
  addItemsToBacklog,
  setOpen
}: {
  addItemsToBacklog: (items: TabSymbol[]) => void;
  setOpen: (open: boolean) => void;
}) => {
  return (
    <FilterProvider disableSearchParams>
      <CatalogModalBase
        addItemsToBacklog={addItemsToBacklog}
        setOpen={setOpen}
      />
    </FilterProvider>
  );
};
